import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchRepairById,
  updateRepair,
  updateRepairStatus,
  addRepairDevice,
  removeRepairDevice,
  addRepairAssignee,
  removeRepairAssignee,
  addRepairPart,
  removeRepairPart,
  deleteRepair,
  clearCurrentRepair,
} from '../redux/slices/repairsSlice';
import { fetchCustomers } from '../redux/slices/customersSlice';
import { fetchDealers } from '../redux/slices/dealersSlice';
import { fetchDevices } from '../redux/slices/devicesSlice';
import { fetchParts, createPart } from '../redux/slices/partsSlice';
import { repairsAPI, usersAPI } from '../api/api';
import { LoadingSpinner, ConfirmDialog, SearchCreateSelect } from '../components/Common';
import EntityLookup, { EntityFormModal } from '../components/Common/EntityLookup';
import { useEntityDetails } from '../context/EntityDetailsContext';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { buildReportId } from '../utils/reportId';

const STATUSES = [
  'warten auf Gerät',
  'bereit zur Analyse',
  'warten auf KV',
  'warten auf Ersatzteil',
  'in Reparatur',
  'zu verrechnen',
  'abgeschlossen',
];

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}


const statusColors = {
  test: 'bg-gray-100 text-gray-800',
  'warten auf Gerät': 'bg-gray-100 text-gray-800',
  'bereit zur Analyse': 'bg-blue-100 text-blue-800',
  'warten auf KV': 'bg-red-100 text-red-800',
  'warten auf Ersatzteil': 'bg-red-100 text-red-800',
  'in Reparatur': 'bg-blue-100 text-blue-800',
  'zu verrechnen': 'bg-yellow-100 text-yellow-800',
  abgeschlossen: 'bg-green-100 text-green-800',
};

const RepairReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    currentRepair: ticket,
    detailLoading: loading,
    detailError: error,
  } = useSelector((state) => state.repairs);
  const { items: customers } = useSelector((state) => state.customers);
  const { items: dealers } = useSelector((state) => state.dealers);
  const { items: devices } = useSelector((state) => state.devices);
  const { items: parts } = useSelector((state) => state.parts);
  const { openCustomer, openDealer, openDevice, openPart, isOpen: isDrawerOpen } = useEntityDetails();
  const { t, language } = useLanguage();
  const dateTimeLocale = language === 'de' ? 'de-DE' : 'en-GB';
  const { user: currentUser } = useAuth();

  const [users, setUsers] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  // Device create/edit form: { mode: 'new' | 'edit', initial }
  const [deviceForm, setDeviceForm] = useState(null);
  const [details, setDetails] = useState(null);
  const [statusNotes, setStatusNotes] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [deviceSelection, setDeviceSelection] = useState(null);
  // Defaults to the logged-in user so assigning yourself is a single click
  // instead of hunting your own name in the dropdown every time.
  const [assigneeId, setAssigneeId] = useState(currentUser?.id ?? '');
  const [partForm, setPartForm] = useState({ part_id: '', quantity: 1 });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saveStatus, setSaveStatus] = useState(''); // '', 'pending', 'saving', 'saved', 'error'
  const [saveError, setSaveError] = useState('');

  const saveTimeoutRef = useRef(null);
  const skipNextAutosave = useRef(true); // true right after (re)loading details from the server
  const wasDrawerOpen = useRef(false);

  const refresh = useCallback(() => dispatch(fetchRepairById(id)), [dispatch, id]);
  const refreshAttachments = useCallback(() => {
    repairsAPI
      .getAttachments(id)
      .then((res) => setAttachments(res.data.data || []))
      .catch(() => setAttachments([]));
  }, [id]);

  // The customer/dealer/device/part detail drawer (opened via Edit ↗ links
  // on this page) saves through its own entity's Redux slice, which never
  // touches this ticket's own fetched snapshot (customer_name, the joined
  // devices array, etc.) -- so without this, an edit made from the drawer
  // only shows up here after a manual page reload. Re-fetching the ticket
  // whenever the drawer closes covers every entity type it can open,
  // including ones added later, without each edit path needing its own
  // callback back into this page.
  useEffect(() => {
    if (wasDrawerOpen.current && !isDrawerOpen) {
      refresh();
    }
    wasDrawerOpen.current = isDrawerOpen;
  }, [isDrawerOpen, refresh]);

  useEffect(() => {
    refresh();
    refreshAttachments();
    dispatch(fetchCustomers());
    dispatch(fetchDealers());
    dispatch(fetchDevices());
    dispatch(fetchParts());
    usersAPI.getAll().then((res) => setUsers(res.data.data || [])).catch(() => setUsers([]));
    return () => {
      dispatch(clearCurrentRepair());
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [dispatch, refresh, refreshAttachments]);

  useEffect(() => {
    if (ticket) {
      skipNextAutosave.current = true;
      setDetails({
        type: ticket.type,
        dealer_id: ticket.dealer_id || '',
        customer_id: ticket.customer_id || '',
        accessories: ticket.accessories || '',
        problem_description: ticket.problem_description || '',
        internal_notes: ticket.internal_notes || '',
        external_reference: ticket.external_reference || '',
        dealer_reference: ticket.dealer_reference || '',
        warranty: !!ticket.warranty,
        cost_estimate_requested: !!ticket.cost_estimate_requested,
        hide_price_for_customer: !!ticket.hide_price_for_customer,
        shipping: ticket.shipping,
        invoicing: ticket.invoicing,
        // '' (unset) must become null before hitting the API -- `carrier` is
        // a nullable MySQL enum, and MySQL's strict mode rejects '' as an
        // invalid enum value (this was silently failing every save when
        // carrier was unset, which is the default for a new ticket).
        carrier: ticket.carrier || '',
        labor_cost: ticket.labor_cost,
        small_material_cost: ticket.small_material_cost,
        shipping_cost: ticket.shipping_cost,
        discount_percent: ticket.discount_percent ?? 0,
        ship_custom: !!ticket.ship_custom,
        ship_name: ticket.ship_name || '',
        ship_street: ticket.ship_street || '',
        ship_zip: ticket.ship_zip || '',
        ship_city: ticket.ship_city || '',
      });
      setNewStatus(ticket.status);
    }
  }, [ticket]);

  const saveDetails = useCallback(
    (detailsToSave) => {
      setSaveStatus('saving');
      setSaveError('');
      dispatch(
        updateRepair({
          id,
          data: {
            ...detailsToSave,
            dealer_id: detailsToSave.dealer_id || null,
            customer_id: detailsToSave.customer_id || null,
            carrier: detailsToSave.carrier || null,
          },
        }),
      )
        .unwrap()
        .then(() => {
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus((s) => (s === 'saved' ? '' : s)), 2000);
        })
        .catch((err) => {
          setSaveStatus('error');
          setSaveError(err?.error || err?.message || t('repairReport.failedToSaveGeneric'));
        });
    },
    [dispatch, id, t],
  );

  // Auto-save: debounced ~1s after the last change to any field. Skipped
  // right after details are (re)loaded from the server so loading a ticket
  // doesn't immediately re-save it.
  useEffect(() => {
    if (!details) return;
    if (skipNextAutosave.current) {
      skipNextAutosave.current = false;
      return;
    }
    setSaveStatus('pending');
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveDetails(details), 1000);
    return () => clearTimeout(saveTimeoutRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  if (loading && !ticket) return <LoadingSpinner />;
  if (error) {
    return (
      <div className='card bg-red-50 border border-red-200'>
        <p className='text-red-600'>{t('repairReport.errorLoadingTicket', { error })}</p>
      </div>
    );
  }
  if (!ticket || !details) return null;

  const customerOptions = customers.map((c) => ({ value: c.id, label: c.name }));
  const dealerOptions = dealers.map((d) => ({ value: d.id, label: d.name }));
  const attachedDeviceIds = new Set((ticket.devices || []).map((d) => d.id));
  const deviceOptions = devices
    .filter((d) => !attachedDeviceIds.has(d.id))
    .map((d) => ({
      value: d.id,
      label: `${d.serial_number}${d.brand || d.model ? ` — ${[d.brand, d.model].filter(Boolean).join(' ')}` : ''}`,
    }));
  const partOptions = parts.map((p) => ({
    value: p.id,
    label: `${p.name} ${p.color ? `(${p.color})` : ''} - Stock: ${p.quantity}`,
    part: p,
  }));

  // react-select's default filter only matches against the visible label
  // (name + color), so searching by supplier never found anything. This
  // matches name, color, supplier, and SKU instead, tokenized so word
  // order in the search box doesn't matter (e.g. "black acme" still finds
  // "Acme Widget (Black)").
  const filterPartOption = (option, rawInput) => {
    const tokens = rawInput.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return true;
    const p = option.data.part;
    // The synthetic "create new part" option has no part data -- never
    // filter it out, or typing a name with no match hides the only way
    // to create it.
    if (!p) return true;
    const haystack = [p?.name, p?.color, p?.supplier, p?.sku, p?.category]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return tokens.every((token) => haystack.includes(token));
  };

  const partsTotal = (ticket.parts_used || []).reduce(
    (sum, p) => sum + p.quantity * Number(p.unit_price),
    0,
  );
  const subtotal =
    partsTotal + Number(ticket.labor_cost) + Number(ticket.small_material_cost) + Number(ticket.shipping_cost);
  const discountPercent = Number(ticket.discount_percent) || 0;
  const discountAmount = Math.round(subtotal * discountPercent) / 100;
  const grandTotal = subtotal - discountAmount;

  const handleSaveDetails = (e) => {
    e.preventDefault();
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveDetails(details);
  };

  const handleStatusUpdate = (e) => {
    e.preventDefault();
    dispatch(updateRepairStatus({ id, status: newStatus, notes: statusNotes || null }))
      .unwrap()
      .then((payload) => {
        setStatusNotes('');
        refresh();
        if (payload.needsManualCompletion) {
          window.alert(t('repairs.invoiceDraftedAlert'));
        }
      })
      .catch((err) => alert(err?.error || t('repairs.updateStatusFailed')));
  };

  const handleAttachDevice = (deviceId) => {
    if (!deviceId) return;
    dispatch(addRepairDevice({ id, data: { device_id: deviceId } }))
      .unwrap()
      .then(() => {
        setDeviceSelection(null);
        refresh();
        dispatch(fetchDevices());
      })
      .catch((err) => console.error('Failed to attach device:', err));
  };

  const handleRemoveDevice = (deviceId) => {
    dispatch(removeRepairDevice({ id, deviceId }))
      .unwrap()
      .then(refresh)
      .catch((err) => console.error('Failed to remove device:', err));
  };

  const handleAddAssignee = (e) => {
    e.preventDefault();
    if (!assigneeId) return;
    dispatch(addRepairAssignee({ id, userId: assigneeId }))
      .unwrap()
      .then(() => {
        setAssigneeId('');
        refresh();
      })
      .catch((err) => console.error('Failed to add assignee:', err));
  };

  const handleRemoveAssignee = (userId) => {
    dispatch(removeRepairAssignee({ id, userId }))
      .unwrap()
      .then(refresh)
      .catch((err) => console.error('Failed to remove assignee:', err));
  };

  const handleAddPart = (e) => {
    e.preventDefault();
    if (!partForm.part_id) return;
    dispatch(addRepairPart({ id, data: { part_id: partForm.part_id, quantity: partForm.quantity } }))
      .unwrap()
      .then(() => {
        setPartForm({ part_id: '', quantity: 1 });
        refresh();
        dispatch(fetchParts());
      })
      .catch((err) => alert(err?.error || t('repairReport.failedToAddPart')));
  };

  const handleRemovePart = (partsUsedId) => {
    dispatch(removeRepairPart({ id, partsUsedId }))
      .unwrap()
      .then(() => {
        refresh();
        dispatch(fetchParts());
      })
      .catch((err) => console.error('Failed to remove part:', err));
  };

  const handleDelete = () => {
    dispatch(deleteRepair(id))
      .unwrap()
      .then(() => navigate('/repairs'))
      .catch((err) => alert(err?.error || t('repairReport.failedToDeleteTicket')));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadError('');
    setUploading(true);
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    repairsAPI
      .uploadAttachments(id, formData)
      .then(() => refreshAttachments())
      .catch((err) => setUploadError(err?.response?.data?.error || t('repairReport.uploadFailed')))
      .finally(() => {
        setUploading(false);
        e.target.value = '';
      });
  };

  const handleDeleteAttachment = (attachmentId) => {
    repairsAPI
      .deleteAttachment(id, attachmentId)
      .then(() => refreshAttachments())
      .catch((err) => console.error('Failed to delete attachment:', err));
  };

  const openDocument = (focus) => {
    window.open(repairsAPI.getDocumentUrl(id, focus), '_blank');
  };

  const handleShiplabel = () => {
    repairsAPI
      .getShiplabel(id)
      .then((res) => window.open(res.data.data.url, '_blank'))
      .catch((err) => {
        const msg = err.response?.data?.error || t('repairReport.failedToGenerateShiplabel');
        alert(msg);
      });
  };

  return (
    <div>
      <Link to='/repairs' className='text-sm text-blue-600 hover:text-blue-800'>
        {t('repairReport.backToRepairs')}
      </Link>

      <div className='flex flex-wrap justify-between items-center mt-2 mb-6 gap-3'>
        <div className='flex items-center gap-3'>
          <h1 className='text-3xl font-bold text-gray-900'>{buildReportId(ticket.id, ticket.created_at)}</h1>
          <span
            className={`inline-block px-3 py-1 text-sm font-medium rounded-full ${statusColors[ticket.status] || 'bg-gray-100 text-gray-800'}`}
          >
            {ticket.status}
          </span>
          {/* Read-only -- set by the ERP document-creation flow, never editable here. */}
          {ticket.invoice_doc_id && (
            <span className='text-sm text-gray-500'>
              {t('repairReport.erpDocIdLabel')}: {ticket.invoice_doc_id}
            </span>
          )}
        </div>
        <div className='flex flex-wrap gap-2'>
          <button onClick={() => openDocument('customer')} className='btn-secondary'>
            {t('repairReport.printCustomer')}
          </button>
          <button onClick={() => openDocument('dealer')} className='btn-secondary'>
            {t('repairReport.printDealer')}
          </button>
          {/* Only once a document exists to reference -- the shiplabel's
              &ref= is that document's id (see buildShiplabelUrl), and the
              backend refuses the request until then too. */}
          {ticket.invoice_doc_id && (
            <button onClick={handleShiplabel} className='btn-secondary'>
              {t('repairReport.shiplabel')}
            </button>
          )}
          <button onClick={() => setShowDeleteConfirm(true)} className='btn-danger'>
            {t('common.delete')}
          </button>
        </div>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        {/* Left column: details + parts + notes */}
        <div className='lg:col-span-2 space-y-6'>
          {/* Status change */}
          <form onSubmit={handleStatusUpdate} className='card'>
            <h3 className='text-lg font-semibold text-gray-900 mb-3'>{t('common.status')}</h3>
            <div className='flex flex-wrap gap-3 items-end'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('repairReport.newStatusLabel')}</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className='input-field'
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className='flex-1 min-w-[200px]'>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('repairReport.notesOptional')}</label>
                <input
                  type='text'
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  className='input-field'
                />
              </div>
              <button type='submit' className='btn-primary'>
                {t('repairReport.updateStatus')}
              </button>
            </div>
          </form>

          {/* Editable ticket details -- autosaves ~1s after you stop typing */}
          <form onSubmit={handleSaveDetails} className='card space-y-4'>
            <div className='flex items-center justify-between'>
              <h3 className='text-lg font-semibold text-gray-900'>{t('repairReport.ticketDetails')}</h3>
              <span className='text-sm'>
                {saveStatus === 'pending' && <span className='text-gray-400'>{t('repairReport.unsavedChanges')}</span>}
                {saveStatus === 'saving' && <span className='text-gray-500'>{t('common.saving')}</span>}
                {saveStatus === 'saved' && <span className='text-green-600'>{t('repairReport.savedCheck')}</span>}
                {saveStatus === 'error' && (
                  <span className='text-red-600'>{t('repairReport.failedToSave', { error: saveError })}</span>
                )}
              </span>
            </div>

            <div className='grid grid-cols-3 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('common.type')}</label>
                <select
                  value={details.type}
                  onChange={(e) => setDetails({ ...details, type: e.target.value })}
                  className='input-field'
                >
                  <option value='Reparatur'>Reparatur</option>
                  <option value='Kontrolle'>Kontrolle</option>
                  <option value='Ersatzteile'>Ersatzteile</option>
                </select>
              </div>
              <div>
                <EntityLookup
                  kind='customer'
                  label={t('repairs.fieldCustomer')}
                  records={customers}
                  options={customerOptions}
                  value={details.customer_id ? parseInt(details.customer_id) : null}
                  onChange={(cid) => setDetails((d) => ({ ...d, customer_id: cid || '' }))}
                  placeholder={t('repairs.searchOrCreateCustomer')}
                  extra={
                    details.customer_id ? (
                      <button
                        type='button'
                        onClick={() => openCustomer(details.customer_id)}
                        className='text-xs text-blue-600 hover:text-blue-800 font-normal'
                      >
                        ↗
                      </button>
                    ) : null
                  }
                />
              </div>
              <div>
                <EntityLookup
                  kind='dealer'
                  label={t('repairs.fieldDealer')}
                  records={dealers}
                  options={dealerOptions}
                  value={details.dealer_id ? parseInt(details.dealer_id) : null}
                  onChange={(did) => setDetails((d) => ({ ...d, dealer_id: did || '' }))}
                  placeholder={t('repairs.searchOrCreateDealer')}
                  extra={
                    details.dealer_id ? (
                      <button
                        type='button'
                        onClick={() => openDealer(details.dealer_id)}
                        className='text-xs text-blue-600 hover:text-blue-800 font-normal'
                      >
                        ↗
                      </button>
                    ) : null
                  }
                />
              </div>
            </div>

            <div className='grid grid-cols-3 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('repairReport.shippingLabel')}</label>
                <select
                  value={details.shipping}
                  onChange={(e) => {
                    const shipping = e.target.value;
                    if (shipping === 'Abholung') {
                      // Picked up in person -- no carrier and no shipping
                      // cost, enforced (the backend forces these too, can't
                      // be overridden). Clear both right away rather than
                      // waiting for the round trip, so the fields don't
                      // briefly show stale values before autosave comes back.
                      setDetails({ ...details, shipping, carrier: '', shipping_cost: 0 });
                    } else if (shipping === 'Händler' || shipping === 'Kunde') {
                      // Sensible default carrier for that shipping method,
                      // NOT enforced -- unlike Abholung, staff can still
                      // change or clear it manually afterward and the
                      // backend never forces it back.
                      setDetails({ ...details, shipping, carrier: shipping === 'Händler' ? 'DHL' : 'POST' });
                    } else {
                      setDetails({ ...details, shipping });
                    }
                  }}
                  className='input-field'
                >
                  <option value='Abholung'>Abholung</option>
                  <option value='Kunde'>Kunde</option>
                  <option value='Händler'>Händler</option>
                </select>
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('repairReport.invoicingLabel')}</label>
                <select
                  value={details.invoicing}
                  onChange={(e) => setDetails({ ...details, invoicing: e.target.value })}
                  className='input-field'
                >
                  <option value='Kunde'>Kunde</option>
                  <option value='Händler'>Händler</option>
                </select>
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('repairReport.carrierLabel')}</label>
                <select
                  value={details.shipping === 'Abholung' ? '' : details.carrier}
                  onChange={(e) => setDetails({ ...details, carrier: e.target.value })}
                  disabled={details.shipping === 'Abholung'}
                  className='input-field disabled:bg-gray-100 disabled:text-gray-400'
                >
                  <option value=''>-</option>
                  <option value='POST'>POST</option>
                  <option value='DHL'>DHL</option>
                </select>
              </div>
            </div>

            {details.shipping !== 'Abholung' && (() => {
              // Default ship-to is whoever `shipping` points at; an external
              // address can override it for shipping only.
              const shipToRecord =
                details.shipping === 'Händler'
                  ? dealers.find((d) => d.id === parseInt(details.dealer_id))
                  : customers.find((c) => c.id === parseInt(details.customer_id));
              const defaultLines = shipToRecord
                ? [
                    shipToRecord.name,
                    [shipToRecord.street, shipToRecord.house_number].filter(Boolean).join(' '),
                    [shipToRecord.zip, shipToRecord.city].filter(Boolean).join(' '),
                  ].filter(Boolean)
                : [];
              return (
                <div className='bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-3'>
                  <div>
                    <div className='text-sm font-medium text-gray-700 mb-1'>{t('repairReport.shipAddressLabel')}</div>
                    <div className={`text-sm ${details.ship_custom ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                      {!shipToRecord
                        ? t('repairReport.shipAddressSelectFirst')
                        : defaultLines.length > 1
                          ? defaultLines.join(', ')
                          : `${shipToRecord.name || ''} – ${t('repairReport.shipAddressNone')}`}
                    </div>
                  </div>
                  <label className='flex items-center gap-2 text-sm text-gray-700'>
                    <input
                      type='checkbox'
                      checked={details.ship_custom}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        // Prefill an empty external address with the name of the
                        // default recipient so only what differs needs typing.
                        setDetails({
                          ...details,
                          ship_custom: checked,
                          ship_name: checked && !details.ship_name ? shipToRecord?.name || '' : details.ship_name,
                        });
                      }}
                    />
                    {t('repairReport.shipCustomLabel')}
                  </label>
                  {details.ship_custom && (
                    <div className='grid grid-cols-4 gap-3'>
                      <input
                        className='input-field col-span-4'
                        placeholder={t('repairReport.shipName')}
                        value={details.ship_name}
                        onChange={(e) => setDetails({ ...details, ship_name: e.target.value })}
                      />
                      <input
                        className='input-field col-span-4'
                        placeholder={t('repairReport.shipStreet')}
                        value={details.ship_street}
                        onChange={(e) => setDetails({ ...details, ship_street: e.target.value })}
                      />
                      <input
                        className='input-field col-span-1'
                        placeholder={t('repairReport.shipZip')}
                        value={details.ship_zip}
                        onChange={(e) => setDetails({ ...details, ship_zip: e.target.value })}
                      />
                      <input
                        className='input-field col-span-3'
                        placeholder={t('repairReport.shipCity')}
                        value={details.ship_city}
                        onChange={(e) => setDetails({ ...details, ship_city: e.target.value })}
                      />
                    </div>
                  )}
                </div>
              );
            })()}

            <div className='flex flex-wrap gap-6'>
              <label className='flex items-center gap-2 text-sm text-gray-700'>
                <input
                  type='checkbox'
                  checked={details.warranty}
                  onChange={(e) => setDetails({ ...details, warranty: e.target.checked })}
                />
                {t('repairReport.warranty')}
              </label>
              <label className='flex items-center gap-2 text-sm text-gray-700'>
                <input
                  type='checkbox'
                  checked={details.cost_estimate_requested}
                  onChange={(e) => setDetails({ ...details, cost_estimate_requested: e.target.checked })}
                />
                {t('repairReport.costEstimateRequested')}
              </label>
              <label className='flex items-center gap-2 text-sm text-gray-700'>
                <input
                  type='checkbox'
                  checked={details.hide_price_for_customer}
                  onChange={(e) => setDetails({ ...details, hide_price_for_customer: e.target.checked })}
                />
                {t('repairReport.hidePriceForCustomer')}
              </label>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>{t('repairs.fieldAccessories')}</label>
              <input
                type='text'
                value={details.accessories}
                onChange={(e) => setDetails({ ...details, accessories: e.target.value })}
                className='input-field'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>{t('repairs.fieldProblemDescription')}</label>
              <textarea
                value={details.problem_description}
                onChange={(e) => setDetails({ ...details, problem_description: e.target.value })}
                className='input-field'
                rows='3'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>{t('common.notes')}</label>
              <textarea
                value={details.internal_notes}
                onChange={(e) => setDetails({ ...details, internal_notes: e.target.value })}
                className='input-field'
                rows='2'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>{t('repairReport.externalReference')}</label>
              <input
                type='text'
                value={details.external_reference}
                onChange={(e) => setDetails({ ...details, external_reference: e.target.value })}
                className='input-field'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>{t('repairReport.dealerReference')}</label>
              <input
                type='text'
                value={details.dealer_reference}
                onChange={(e) => setDetails({ ...details, dealer_reference: e.target.value })}
                className='input-field'
              />
              <p className='text-xs text-gray-500 mt-1'>
                {t('repairReport.dealerReferenceHint')}
              </p>
            </div>

            <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('repairReport.laborCost')}</label>
                <input
                  type='number'
                  step='0.01'
                  value={details.labor_cost}
                  onChange={(e) => setDetails({ ...details, labor_cost: e.target.value })}
                  className='input-field'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('repairReport.smallMaterialCost')}</label>
                <input
                  type='number'
                  step='0.01'
                  value={details.small_material_cost}
                  onChange={(e) => setDetails({ ...details, small_material_cost: e.target.value })}
                  className='input-field'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('repairReport.shippingCost')}</label>
                <input
                  type='number'
                  step='0.01'
                  value={details.shipping === 'Abholung' ? 0 : details.shipping_cost}
                  onChange={(e) => setDetails({ ...details, shipping_cost: e.target.value })}
                  disabled={details.shipping === 'Abholung'}
                  className='input-field disabled:bg-gray-100 disabled:text-gray-400'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('repairReport.discountPercent')}</label>
                <input
                  type='number'
                  step='0.01'
                  min='0'
                  max='100'
                  value={details.discount_percent}
                  onChange={(e) => setDetails({ ...details, discount_percent: e.target.value })}
                  className='input-field'
                />
              </div>
            </div>

            <div className='flex justify-end'>
              <button type='submit' className='btn-primary'>
                {t('repairReport.saveDetails')}
              </button>
            </div>
          </form>

          {/* Parts used */}
          <div className='card'>
            <h3 className='text-lg font-semibold text-gray-900 mb-3'>{t('repairReport.partsUsed')}</h3>
            <form onSubmit={handleAddPart} className='bg-gray-50 p-4 rounded-lg grid grid-cols-4 gap-3 mb-4'>
              <div className='col-span-2'>
                {partForm.part_id && (
                  <div className='flex justify-end mb-1'>
                    <button
                      type='button'
                      onClick={() => openPart(parseInt(partForm.part_id), null, { startInEdit: true })}
                      className='text-xs text-blue-600 hover:text-blue-800 font-normal'
                    >
                      {t('repairReport.editArrow')}
                    </button>
                  </div>
                )}
                <SearchCreateSelect
                  options={partOptions}
                  value={partForm.part_id ? parseInt(partForm.part_id) : null}
                  onChange={(pid) => setPartForm({ ...partForm, part_id: pid ? pid.toString() : '' })}
                  onCreate={currentUser?.userType === 'admin' ? async (name) => {
                    const created = await dispatch(createPart({ name })).unwrap();
                    return created.id;
                  } : undefined}
                  filterOption={filterPartOption}
                  placeholder={t('repairReport.searchOrCreatePart')}
                />
              </div>
              <input
                type='number'
                min='1'
                value={partForm.quantity}
                onChange={(e) => setPartForm({ ...partForm, quantity: e.target.value })}
                className='input-field'
              />
              <button type='submit' className='btn-success'>
                {t('common.add')}
              </button>
            </form>

            {(!ticket.parts_used || ticket.parts_used.length === 0) ? (
              <div className='text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300'>
                <p className='text-gray-500'>{t('repairReport.noPartsRecorded')}</p>
              </div>
            ) : (
              <div className='border rounded-lg overflow-hidden'>
                <table className='min-w-full divide-y divide-gray-200'>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('repairReport.partCol')}</th>
                      <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('repairReport.qtyCol')}</th>
                      <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('repairReport.unitPriceCol')}</th>
                      <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('repairReport.amountCol')}</th>
                      <th className='px-4 py-2 text-right text-xs font-medium text-gray-500'>{t('repairReport.actionCol')}</th>
                    </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                    {ticket.parts_used.map((p) => (
                      <tr key={p.id}>
                        <td className='px-4 py-2 text-sm font-medium'>
                          {p.part_id ? (
                            <button
                              type='button'
                              onClick={() => openPart(p.part_id)}
                              className='text-blue-600 hover:text-blue-800 hover:underline'
                            >
                              {p.part_name}
                              {p.part_color ? ` (${p.part_color})` : ''}
                            </button>
                          ) : (
                            <span className='text-gray-700 italic' title={t('repairReport.importedFromNotion')}>
                              {p.part_name}
                              {p.part_color ? ` (${p.part_color})` : ''}
                            </span>
                          )}
                        </td>
                        <td className='px-4 py-2 text-sm'>{p.quantity}</td>
                        <td className='px-4 py-2 text-sm'>CHF {Number(p.unit_price).toFixed(2)}</td>
                        <td className='px-4 py-2 text-sm'>
                          CHF {(p.quantity * Number(p.unit_price)).toFixed(2)}
                        </td>
                        <td className='px-4 py-2 text-sm text-right'>
                          <button
                            type='button'
                            onClick={() => handleRemovePart(p.id)}
                            className='text-red-600 hover:text-red-900'
                          >
                            {t('common.remove')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className='mt-4 text-sm text-gray-700 space-y-1'>
              <div className='flex justify-between max-w-xs ml-auto'>
                <span>{t('repairReport.partsLabel')}</span>
                <span>CHF {partsTotal.toFixed(2)}</span>
              </div>
              <div className='flex justify-between max-w-xs ml-auto'>
                <span>{t('repairReport.laborLabel')}</span>
                <span>CHF {Number(ticket.labor_cost).toFixed(2)}</span>
              </div>
              <div className='flex justify-between max-w-xs ml-auto'>
                <span>{t('repairReport.smallMaterialLabel')}</span>
                <span>CHF {Number(ticket.small_material_cost).toFixed(2)}</span>
              </div>
              <div className='flex justify-between max-w-xs ml-auto'>
                <span>{t('repairReport.shippingLabel')}</span>
                <span>CHF {Number(ticket.shipping_cost).toFixed(2)}</span>
              </div>
              {discountPercent > 0 && (
                <div className='flex justify-between max-w-xs ml-auto text-green-700'>
                  <span>{t('repairReport.discountLabel', { percent: discountPercent })}</span>
                  <span>-CHF {discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className='flex justify-between max-w-xs ml-auto font-bold text-gray-900 border-t pt-1'>
                <span>{t('common.total')}</span>
                <span>CHF {grandTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Attachments */}
          <div className='card'>
            <h3 className='text-lg font-semibold text-gray-900 mb-3'>{t('repairReport.attachments')}</h3>
            <div className='mb-4'>
              <label className='btn-secondary inline-block cursor-pointer'>
                {uploading ? t('repairReport.uploading') : t('repairReport.addFiles')}
                <input
                  type='file'
                  multiple
                  accept='image/*,.pdf,.doc,.docx'
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className='hidden'
                />
              </label>
              <span className='ml-3 text-xs text-gray-500'>
                {t('repairReport.imagesCompressedHint')}
              </span>
              {uploadError && <p className='text-sm text-red-600 mt-2'>{uploadError}</p>}
            </div>

            {attachments.length === 0 ? (
              <p className='text-gray-500 text-sm'>{t('repairReport.noFilesAttached')}</p>
            ) : (
              <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3'>
                {attachments.map((a) => {
                  const url = repairsAPI.getAttachmentUrl(id, a.id);
                  const isImage = a.mime_type.startsWith('image/');
                  return (
                    <div key={a.id} className='border rounded-lg overflow-hidden bg-gray-50'>
                      <a href={url} target='_blank' rel='noreferrer' className='block'>
                        {isImage ? (
                          <img src={url} alt={a.original_name} className='w-full h-24 object-cover' />
                        ) : (
                          <div className='w-full h-24 flex items-center justify-center text-4xl'>📄</div>
                        )}
                      </a>
                      <div className='p-2'>
                        <p className='text-xs text-gray-700 truncate' title={a.original_name}>
                          {a.original_name}
                        </p>
                        <div className='flex items-center justify-between mt-1'>
                          <span className='text-xs text-gray-400'>{formatFileSize(a.size_bytes)}</span>
                          <button
                            type='button'
                            onClick={() => handleDeleteAttachment(a.id)}
                            className='text-xs text-red-600 hover:text-red-800'
                          >
                            {t('common.remove')}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Status history */}
          <div className='card'>
            <h3 className='text-lg font-semibold text-gray-900 mb-3'>{t('repairReport.statusHistory')}</h3>
            {(!ticket.status_history || ticket.status_history.length === 0) ? (
              <p className='text-gray-500 text-sm'>{t('repairReport.noHistoryYet')}</p>
            ) : (
              <ul className='space-y-2'>
                {ticket.status_history.map((h) => (
                  <li key={h.id} className='text-sm border-l-2 border-gray-200 pl-3'>
                    <span className='font-medium text-gray-900'>
                      {h.old_status ? `${h.old_status} → ${h.new_status}` : h.new_status}
                    </span>{' '}
                    <span className='text-gray-500'>
                      &middot; {new Date(h.created_at).toLocaleString(dateTimeLocale)}
                      {h.changed_by_name ? ` · ${h.changed_by_name}` : ''}
                    </span>
                    {h.notes && <p className='text-gray-600'>{h.notes}</p>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Right column: devices + assignees */}
        <div className='space-y-6'>
          <div className='card'>
            <h3 className='text-lg font-semibold text-gray-900 mb-3'>{t('repairReport.devicesHeading')}</h3>
            {(!ticket.devices || ticket.devices.length === 0) ? (
              <p className='text-gray-500 text-sm mb-3'>{t('repairReport.noDevicesAttached')}</p>
            ) : (
              <ul className='space-y-2 mb-3'>
                {ticket.devices.map((d) => (
                  <li key={d.id} className='flex justify-between items-center text-sm border rounded-lg p-2'>
                    <button
                      type='button'
                      onClick={() => openDevice(d.id, d)}
                      className='text-left text-blue-600 hover:text-blue-800 hover:underline'
                    >
                      {d.brand} {d.model} <span className='text-gray-500'>(SN: {d.serial_number})</span>
                    </button>
                    <span className='flex items-center gap-3'>
                      <button
                        type='button'
                        onClick={() => setDeviceForm({ mode: 'edit', initial: d })}
                        className='text-blue-600 hover:text-blue-800 text-xs'
                      >
                        ✏️ {t('common.edit')}
                      </button>
                      <button
                        onClick={() => handleRemoveDevice(d.id)}
                        className='text-red-600 hover:text-red-900 text-xs'
                      >
                        {t('common.remove')}
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <SearchCreateSelect
              options={deviceOptions}
              value={deviceSelection}
              onChange={(deviceId) => {
                setDeviceSelection(deviceId);
                handleAttachDevice(deviceId);
              }}
              onCreate={(serial) => {
                // Open the full device form so brand / model can be filled in right away.
                setDeviceForm({ mode: 'new', initial: { serial_number: serial } });
                return null;
              }}
              placeholder={t('repairReport.searchSerialOrCreate')}
            />
            <EntityFormModal
              kind='device'
              mode={deviceForm?.mode}
              initial={deviceForm?.initial}
              onClose={() => setDeviceForm(null)}
              onSaved={(device) => {
                if (deviceForm.mode === 'new') handleAttachDevice(device.id);
                else refresh();
                setDeviceForm(null);
              }}
            />
          </div>

          <div className='card'>
            <h3 className='text-lg font-semibold text-gray-900 mb-3'>{t('repairReport.assignedTo')}</h3>
            {(!ticket.assignees || ticket.assignees.length === 0) ? (
              <p className='text-gray-500 text-sm mb-3'>{t('repairReport.noOneAssigned')}</p>
            ) : (
              <ul className='space-y-2 mb-3'>
                {ticket.assignees.map((a) => (
                  <li key={a.id} className='flex justify-between items-center text-sm border rounded-lg p-2'>
                    <span>{a.full_name}</span>
                    <button
                      onClick={() => handleRemoveAssignee(a.id)}
                      className='text-red-600 hover:text-red-900 text-xs'
                    >
                      {t('common.remove')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <form onSubmit={handleAddAssignee} className='flex gap-2'>
              <select
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
                className='input-field flex-1'
              >
                <option value=''>{t('repairReport.selectEmployee')}</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name}
                  </option>
                ))}
              </select>
              <button type='submit' className='btn-secondary'>
                {t('common.add')}
              </button>
            </form>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title={t('repairReport.deleteTicketTitle')}
        message={t('repairReport.deleteTicketMessage', { ticketId: buildReportId(ticket.id, ticket.created_at) })}
      />

    </div>
  );
};

export default RepairReport;
